import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";
import { Box, TextField, MenuItem } from "@mui/material";
import { SubmitButton } from "@components";
import { getRolesList, isRoleLowerOrEqual } from "features/users/utils";
import { useNotification } from "@hooks/useNotifications";
import type { FormAction, User, UserPUT } from "features/users/types";
import { useAuth } from "features/auth";
import { usersService } from "features/users/services/users-service";
import { UserRoleEnum } from "features/users/types";
import type { LanguageSummary } from "features/languages/types";

import { Multiselect } from "./Multiselect";
import { formRowStyle, formStyle } from "./styles";

const getValidationSchema = (currentRole: number) =>
  yup.object({
    email: yup.string().email("Enter a valid email").required("Email is required"),
    name: yup.string().required("Name is required"),
    role: yup.number().min(currentRole, "You do not have permission to set this role").required("Role is required"),
  });

export const UserForm = ({
  action,
  user,
  hasEditAccess,
  isMe,
}: {
  action?: FormAction;
  hasEditAccess: boolean;
  user: User;
  isMe: boolean;
}) => {
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotification();
  const { userRole } = useAuth();
  const [isUpdating, setIsUpdating] = useState(false);
  const [showLanguages, setShowLanguages] = useState(() => isRoleLowerOrEqual(user.role, UserRoleEnum.Translator));
  const isCreate = action === "create";
  const isEdit = action === "edit";
  const isRead = action === "read";
  const isInputDisabled = isUpdating || !hasEditAccess || isRead;
  const RolesList = useMemo(() => getRolesList(userRole, isMe), [userRole, hasEditAccess, isMe]);

  const validationSchema = useMemo(() => getValidationSchema(userRole), [userRole]);

  const formik = useFormik({
    initialValues: {
      email: user.email,
      name: user.name,
      role: user.role,
      id: user.id,
      languages: user.languages ?? [],
    },
    validationSchema,
    onSubmit: async userValue => {
      setIsUpdating(true);
      try {
        const data: UserPUT = {
          ...userValue,
          languages: userValue.languages.map((language: LanguageSummary) => language.id) as string[],
        };

        const response = await usersService.saveUser(data);

        if (response?.id) {
          showSuccess(isCreate ? "User created" : "User updated");
          navigate(-1);
        } else {
          showError(`User ${isCreate ? "creation" : "update"} failed. Please try again or contact support.`);
        }
      } catch (error) {
        console.error(error);

        let message = `User ${isCreate ? "creation" : "update"} failed. Please try again or contact support.`;

        if (error?.response?.data?.title?.includes?.("already exists")) {
          message = "User with this email already exists.";
        }

        showError(message);
      }
      setIsUpdating(false);
    },
  });

  // Toggle languages panel visibility based on user role
  useEffect(() => {
    setShowLanguages(isRoleLowerOrEqual(formik.values.role, UserRoleEnum.Translator));
  }, [formik.values.role]);

  return (
    <Box sx={formStyle}>
      <Box component="form" onSubmit={formik.handleSubmit}>
        <Box sx={formRowStyle}>
          <TextField
            disabled={isInputDisabled || isEdit}
            required
            id="email"
            label="Email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />
        </Box>
        <Box sx={formRowStyle}>
          <TextField
            disabled={isInputDisabled}
            required
            id="name"
            label="Name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
          />
        </Box>
        <Box sx={formRowStyle}>
          <TextField
            disabled={isInputDisabled || isMe}
            id="role"
            name="role"
            select
            label="User Role"
            value={formik.values.role}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.role && Boolean(formik.errors.role)}
            helperText={formik.touched.role && formik.errors.role}
          >
            {RolesList.map(({ value, label }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box sx={{ display: showLanguages ? "block" : "none" }}>
          <Multiselect
            defaultValue={formik.values.languages}
            onChange={(value: LanguageSummary[]) => {
              formik.setFieldValue("languages", value);
            }}
          />
        </Box>

        {hasEditAccess && (isEdit || isCreate) ? (
          <SubmitButton variant="contained" isProcessing={isUpdating} onClick={() => {}}>
            {(isCreate && "Create") || (isEdit && "Save")}
          </SubmitButton>
        ) : null}
      </Box>
    </Box>
  );
};
